import { useCallback, useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"

export const INTERACTION_DESCRIPTION_ATTRIBUTE = "data-interaction-description-key"

export interface InteractionDescriptionProps {
  descriptionKey?: string
}

const CURSOR_OFFSET = 16
const VIEWPORT_MARGIN = 12

interface PointerPosition {
  x: number
  y: number
}

const getDescriptionElement = (target: EventTarget | null) => {
  if (!(target instanceof Element)) return null

  return target.closest<HTMLElement>(`[${INTERACTION_DESCRIPTION_ATTRIBUTE}]`)
}

export const InteractionTooltip = () => {
  const { t } = useTranslation("interactionDescriptions")

  const [activeDescriptionKey, setActiveDescriptionKey] = useState<string | null>(null)
  const activeDescriptionKeyRef = useRef<string | null>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const pointerPositionRef = useRef<PointerPosition>({ x: 0, y: 0 })
  const animationFrameRef = useRef<number | null>(null)

  const positionTooltip = useCallback(() => {
    animationFrameRef.current = null

    const tooltip = tooltipRef.current
    if (!tooltip) return

    const { x, y } = pointerPositionRef.current
    const bounds = tooltip.getBoundingClientRect()
    const maximumLeft = Math.max(VIEWPORT_MARGIN, window.innerWidth - bounds.width - VIEWPORT_MARGIN)
    const maximumTop = Math.max(VIEWPORT_MARGIN, window.innerHeight - bounds.height - VIEWPORT_MARGIN)
    const left = Math.min(Math.max(VIEWPORT_MARGIN, x + CURSOR_OFFSET), maximumLeft)
    const top = Math.min(Math.max(VIEWPORT_MARGIN, y + CURSOR_OFFSET), maximumTop)

    tooltip.style.transform = `translate3d(${left}px, ${top}px, 0)`
  }, [])

  const schedulePosition = useCallback(
    (x: number, y: number) => {
      pointerPositionRef.current = { x, y }

      if (animationFrameRef.current === null) {
        animationFrameRef.current = window.requestAnimationFrame(positionTooltip)
      }
    },
    [positionTooltip],
  )

  useEffect(() => {
    const showFromPointer = (event: PointerEvent) => {
      if (event.pointerType && event.pointerType !== "mouse") return

      const element = getDescriptionElement(event.target)
      const descriptionKey = element?.getAttribute(INTERACTION_DESCRIPTION_ATTRIBUTE)

      if (!descriptionKey) {
        if (activeDescriptionKeyRef.current !== null) {
          activeDescriptionKeyRef.current = null
          setActiveDescriptionKey(null)
        }

        return
      }

      if (activeDescriptionKeyRef.current !== descriptionKey) {
        activeDescriptionKeyRef.current = descriptionKey
        setActiveDescriptionKey(descriptionKey)
      }

      schedulePosition(event.clientX, event.clientY)
    }

    const hideFromPointer = (event: PointerEvent) => {
      const currentElement = getDescriptionElement(event.target)
      const nextElement = getDescriptionElement(event.relatedTarget)

      if (currentElement && currentElement === nextElement) return
      if (activeDescriptionKeyRef.current === null) return

      activeDescriptionKeyRef.current = null
      setActiveDescriptionKey(null)
    }

    const showFromFocus = (event: FocusEvent) => {
      const element = getDescriptionElement(event.target)
      const descriptionKey = element?.getAttribute(INTERACTION_DESCRIPTION_ATTRIBUTE)

      if (!descriptionKey || !element) return

      const bounds = element.getBoundingClientRect()
      activeDescriptionKeyRef.current = descriptionKey
      setActiveDescriptionKey(descriptionKey)

      schedulePosition(bounds.left, bounds.bottom)
    }

    const hideFromFocus = (event: FocusEvent) => {
      const currentElement = getDescriptionElement(event.target)
      const nextElement = getDescriptionElement(event.relatedTarget)

      if (currentElement && currentElement === nextElement) return
      if (activeDescriptionKeyRef.current === null) return

      activeDescriptionKeyRef.current = null
      setActiveDescriptionKey(null)
    }

    const hideOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && activeDescriptionKeyRef.current !== null) {
        activeDescriptionKeyRef.current = null
        setActiveDescriptionKey(null)
      }
    }

    document.addEventListener("pointerover", showFromPointer, true)
    document.addEventListener("pointermove", showFromPointer, true)
    document.addEventListener("pointerout", hideFromPointer, true)
    document.addEventListener("focusin", showFromFocus, true)
    document.addEventListener("focusout", hideFromFocus, true)
    document.addEventListener("keydown", hideOnEscape, true)

    return () => {
      document.removeEventListener("pointerover", showFromPointer, true)
      document.removeEventListener("pointermove", showFromPointer, true)
      document.removeEventListener("pointerout", hideFromPointer, true)
      document.removeEventListener("focusin", showFromFocus, true)
      document.removeEventListener("focusout", hideFromFocus, true)
      document.removeEventListener("keydown", hideOnEscape, true)

      if (animationFrameRef.current !== null) {
        window.cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [schedulePosition])

  useEffect(() => {
    if (activeDescriptionKey) positionTooltip()
  }, [activeDescriptionKey, positionTooltip])

  if (!activeDescriptionKey) return null

  return createPortal(
    <div
      ref={tooltipRef}
      role="tooltip"
      className="pointer-events-none fixed left-0 top-0 z-[10000] max-w-xs rounded-lg border border-slate-700/20 bg-slate-950 px-3 py-2 text-xs font-medium leading-relaxed text-white shadow-xl"
    >
      {t(activeDescriptionKey)}
    </div>,
    document.body,
  )
}
